import React, { useState, useEffect } from 'react';
import { Search, ExternalLink } from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config'; // Adjust this path to your firebase config
import { createStudentViewPath } from '../../utils/studentRoutes';

const StudentSearchWidget = ({ canViewDashboard, userRole, assignedDivision, assignedBatch }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState('roll');
  const [searchedStudent, setSearchedStudent] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  // INTEGRATED FIRESTORE SEARCH FUNCTION
  const searchStudentInFirestore = async (searchQuery, searchMode) => {
    try {
      console.log('Starting Firestore search:', { searchQuery, searchMode });

      if (!searchQuery || searchQuery.trim() === '') {
        console.log('Empty search query');
        return null;
      }

      const studentsRef = collection(db, 'students');

      if (searchMode === 'roll') {
        // ROLL NUMBER SEARCH - Exact match
        const rollNumber = searchQuery.toUpperCase().trim();
        console.log('Searching for roll number:', rollNumber);
        
        const q = query(studentsRef, where('rollNumber', '==', rollNumber));
        const snapshot = await getDocs(q);
        
        console.log('Roll search - Documents found:', snapshot.size);
        
        if (snapshot.empty) {
          return null;
        }
        
        const student = { 
          id: snapshot.docs[0].id, 
          ...snapshot.docs[0].data() 
        };
        
        console.log('Found student:', student);
        return student;
        
      } else if (searchMode === 'name') {
        // NAME SEARCH - Fetch all and filter (Firestore limitation)
        console.log('Searching by name - Fetching all students');
        
        const snapshot = await getDocs(studentsRef);
        console.log('Total students in database:', snapshot.size);
        
        if (snapshot.empty) {
          return null;
        }
        
        const allStudents = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Case-insensitive partial search in all name fields
        const searchTerm = searchQuery.toLowerCase().trim();
        const matchedStudents = allStudents.filter(student => {
          const firstName = (student.firstName || '').toLowerCase();
          const fatherName = (student.fatherName || '').toLowerCase();
          const surname = (student.surname || '').toLowerCase();
          const fullName = `${firstName} ${fatherName} ${surname}`;
          
          return fullName.includes(searchTerm) || 
                 firstName.includes(searchTerm) || 
                 surname.includes(searchTerm);
        });
        
        console.log('Matched students:', matchedStudents.length);
        
        if (matchedStudents.length === 0) {
          return null;
        }
        
        return matchedStudents;
      }
      
      return null;
      
    } catch (error) {
      console.error('Error searching students in Firestore:', error);
      throw error;
    }
  };

  // Real-time search for name mode (triggers after 3 chars)
  useEffect(() => {
    const performSearch = async () => {
      if (searchMode === 'name' && searchQuery.length >= 3) {
        setIsSearching(true);
        setError(null);
        try {
          console.log('Searching for:', searchQuery, 'Mode:', searchMode);
          const result = await searchStudentInFirestore(searchQuery.toLowerCase(), searchMode);
          console.log('Raw search result:', result);
          console.log('Result type:', Array.isArray(result) ? 'Array' : typeof result);
          console.log('Result length:', Array.isArray(result) ? result.length : 'N/A');
          
          if (!result) {
            console.log('No result returned');
            setSearchedStudent(null);
          } else if (Array.isArray(result)) {
            console.log('Filtering', result.length, 'students by scope');
            // Filter results by scope
            const filtered = result.filter(student => {
              const hasAccess = filterByScope(student);
              console.log(`Student ${student.rollNumber} access:`, hasAccess);
              return hasAccess;
            });
            console.log('Filtered results:', filtered.length);
            setSearchedStudent(filtered.length > 0 ? filtered : null);
          } else {
            // Single student result
            console.log('Single student result:', result.rollNumber);
            const hasAccess = filterByScope(result);
            console.log('Has access:', hasAccess);
            setSearchedStudent(hasAccess ? result : null);
          }
        } catch (error) {
          console.error('Search error:', error);
          setError(`Search failed: ${error.message}`);
          setSearchedStudent(null);
        } finally {
          setIsSearching(false);
        }
      } else if (searchMode === 'name' && searchQuery.length < 3) {
        setSearchedStudent(null);
        setError(null);
      }
    };

    const timeoutId = setTimeout(performSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchMode, userRole, assignedDivision, assignedBatch]);

  const handleSearch = async () => {
    if (searchMode === 'roll' && searchQuery.trim()) {
      setIsSearching(true);
      setError(null);
      try {
        const searchTerm = searchQuery.trim().toUpperCase();
        console.log('Roll search for:', searchTerm);
        const student = await searchStudentInFirestore(searchTerm, searchMode);
        console.log('Roll search result:', student);
        
        if (!student) {
          console.log('No student found for roll number:', searchTerm);
          setSearchedStudent(null);
        } else {
          // Check if student is in user's scope
          const hasAccess = filterByScope(student);
          console.log('Student found, has access:', hasAccess);
          setSearchedStudent(hasAccess ? student : null);
        }
      } catch (error) {
        console.error('Roll search error:', error);
        setError(`Search failed: ${error.message}`);
        setSearchedStudent(null);
      } finally {
        setIsSearching(false);
      }
    }
  };

  const handleQueryChange = (e) => {
    setSearchQuery(e.target.value);
    setError(null);
  };

  const handleModeChange = (e) => {
    setSearchMode(e.target.value);
    setSearchQuery('');
    setSearchedStudent(null);
    setError(null);
  };

  const filterByScope = (student) => {
    if (!student) {
      console.log('filterByScope: No student provided');
      return false;
    }
    
    console.log('Filtering student:', student.rollNumber, {
      userRole,
      studentDivision: student.division,
      assignedDivision,
      studentBatch: student.batch,
      assignedBatch
    });
    
    // HOD and Mentoring Coordinator can see all students
    if (userRole === 'hod' || userRole === 'mentoringCoordinator') {
      console.log('Access granted: HOD/Mentoring Coordinator');
      return true;
    }
    
    // Class Coordinator can see their division
    if (userRole === 'classCoordinator' && student.division === assignedDivision) {
      console.log('Access granted: Class Coordinator - Division match');
      return true;
    }
    
    // Mentor can see their batch
    if (userRole === 'mentor' && student.batch === assignedBatch) {
      console.log('Access granted: Mentor - Batch match');
      return true;
    }
    
    console.log('Access denied: No matching permissions');
    return false;
  };

  const renderStudentCard = (student) => (
    <div key={student.rollNumber} className="border rounded-lg p-4 bg-gray-50 mb-3">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="text-lg font-semibold text-gray-900">
            {student.firstName} {student.fatherName} {student.surname}
          </h4>
          <div className="grid grid-cols-2 gap-2 mt-2 text-sm text-gray-600">
            <div><strong>Roll:</strong> {student.rollNumber}</div>
            <div><strong>Division/Batch:</strong> {student.division} / {student.batch || 'Not Assigned'}</div>
            <div><strong>Mobile Number:</strong> {student.mobile || 'N/A'}</div>
            <div><strong>Branch:</strong> {student.branch || 'N/A'}</div>
            <div><strong>Class Coordinator:</strong> {student.ccName || 'N/A'}</div>
            <div><strong>Mentor:</strong> {student.mentorName || 'N/A'}</div>
          </div>
          {student.attendance && (
            <div className="mt-3 p-3 bg-white rounded">
              <div className="grid grid-cols-3 gap-3 text-center text-sm">
                <div>
                  <div className="font-bold text-gray-900">{student.attendance.theoryPercentage?.toFixed(2) || 0}%</div>
                  <div className="text-gray-600">Theory</div>
                </div>
                <div>
                  <div className="font-bold text-gray-900">{student.attendance.practicalPercentage?.toFixed(2) || 0}%</div>
                  <div className="text-gray-600">Practical</div>
                </div>
                <div>
                  <div className={`font-bold ${(student.attendance.overallPercentage || 0) >= 75 ? 'text-green-600' : 'text-red-600'}`}>
                    {student.attendance.overallPercentage?.toFixed(2) || 0}%
                  </div>
                  <div className="text-gray-600">Overall</div>
                </div>
              </div>
            </div>
          )}
        </div>
        {(canViewDashboard || userRole === 'classCoordinator') && (
          <button
            onClick={() => window.open(createStudentViewPath('/student/mahiti', student.rollNumber), '_blank')}
            className="ml-4 flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 shadow-md hover:shadow-lg transition-all"
          >
            <ExternalLink className="w-4 h-4" />
            <span>More Details</span>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {/* Search Bar */}
      <div className="flex space-x-2 mb-4">
        <select
          value={searchMode}
          onChange={handleModeChange}
          className="px-3 py-2 border rounded-lg"
        >
          <option value="roll">Roll Number</option>
          <option value="name">Name</option>
        </select>
        <input
          type="text"
          value={searchQuery}
          onChange={handleQueryChange}
          placeholder={searchMode === 'roll' ? 'Enter Roll Number' : 'Enter Name (min 3 chars)'}
          className="flex-1 px-4 py-2 border rounded-lg"
          onKeyPress={(e) => e.key === 'Enter' && searchMode === 'roll' && handleSearch()}
        />
        {searchMode === 'roll' && (
          <button
            onClick={handleSearch}
            disabled={!searchQuery.trim() || isSearching}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Search className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading indicator */}
      {isSearching && (
        <div className="text-center py-4 text-gray-500">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <div className="mt-2">Searching...</div>
        </div>
      )}

      {/* Search Results - handles both single student and array */}
      {!isSearching && searchedStudent && (
        Array.isArray(searchedStudent) ? (
          <div>
            <div className="text-sm text-gray-600 mb-2">
              Found {searchedStudent.length} student{searchedStudent.length !== 1 ? 's' : ''}
            </div>
            {searchedStudent.map(student => renderStudentCard(student))}
          </div>
        ) : (
          renderStudentCard(searchedStudent)
        )
      )}

      {/* No Results Messages */}
      {!isSearching && searchedStudent === null && searchQuery && !error && (
        <div className="text-center py-8 text-gray-500">
          {searchMode === 'name' && searchQuery.length < 3 
            ? 'Enter at least 3 characters to search'
            : 'No student found or you do not have permission to view this student'}
        </div>
      )}
    </div>
  );
};

export default StudentSearchWidget;
